import React, { useEffect, useState } from 'react';
import MenuIcon from '@material-ui/icons/Menu';
import { IconButton } from '@material-ui/core';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import { useStyles } from './Navbar.styles';
import Dashboard from '../../../assets/icons/dashboard.svg';
import { useHistory } from 'react-router-dom';
import NavbarMenu from '../NavbarMenu/NavbarMenu.component';
import usePermission from '../../../hooks/usePermission';
import { UserPermission } from '../../../enum/userPermissions';
import { SystemSettings } from '../../SystemSettings/SystemSettings.component';
import { viewType } from '../Layout.types';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { LoaderAlert } from '../LoaderAlert.component';
import { Grid } from '@mui/material';
import { MailMenu } from './MailMenu/MailMenu.component';
import useSessionUser from '../../../hooks/useSessionUser';
import { Paths } from '../../../utils/routerUrls';
import NotificationsMenu from './NotificationsMenu/NotificationsMenu.component';
import { ReactComponent as ContextualHelpIcon } from '../../../assets/icons/contextual-help-menu.svg';
import { ReactComponent as ContextualHelpIconHover } from '../../../assets/icons/contextual-help-menu-hover.svg';
import { useDispatch, useSelector } from 'react-redux';
import { contextualHelpActions } from '../../../reducers/contextualHelpReducer/contextualHelpSlice';
import { RootState } from '../../../store';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import EventNoteIcon from '@mui/icons-material/EventNote';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import EnvWindow from '../../../utils/env';
import { fill } from '../../../utils/contrastSvgAttributes';
import { mailboxActions } from '../../../reducers/mailboxReducer/mailboxSlice';
import { Folder } from '../../../models/communication.types';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { NAVBAR_ISMINIMIZED_KEY } from '../Layout.component';

interface NavbarProp {
  getHomeUrl?: () => string | undefined;
  getAssessmentHomeUrl?: () => string | false | undefined;
  changeSidebarView?: () => void;
  examinerView?: boolean;
  dashboardView?: boolean;
  viewType?: viewType;
  messageModalIsOpen?: (value: boolean) => void;
  isSkipLink?: boolean;
}

const Navbar: React.FC<NavbarProp> = ({
  getHomeUrl,
  getAssessmentHomeUrl,
  changeSidebarView,
  examinerView,
  dashboardView,
  viewType,
  messageModalIsOpen,
  isSkipLink,
}) => {
  const classes = useStyles();
  const { user, userAssessmentSessionInfo } = useSessionUser();
  const history = useHistory();
  const dispatch = useDispatch();

  const [openSystemSettings, setOpenSystemSettings] = useState<boolean>(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>();

  const isContextualHelp = useSelector((state: RootState) => state.contextualHelp.isContextualHelp);

  const handleMenuHamburgerClick = () => {
    changeSidebarView && changeSidebarView();
  };

  const handleMenuHambugerClickMailbox = () => {
    dispatch(mailboxActions.toggleIsMenuMinimized(null));
  };

  useEffect(() => {
    messageModalIsOpen && messageModalIsOpen(isMessageModalOpen);
  }, [isMessageModalOpen]);

  const inMailboxAlready = window.location.href.includes(Paths.mailbox);

  const handleHomeClick = () => {
    const homeUrl: string | false | undefined = examinerView
      ? getAssessmentHomeUrl && getAssessmentHomeUrl()
      : getHomeUrl && getHomeUrl();
    if (homeUrl === false) {
      return;
    } else if (homeUrl) {
      if (inMailboxAlready) {
        dispatch(mailboxActions.changeFolder(Folder.Received));
      }
      history.push(homeUrl);
    } else {
      history.push(Paths.adminDashboard);
    }
  };

  const handleSupportClick = () => {
    window.open(window.location.origin + Paths.support, '_blank');
  };

  const hasAdminSettingsManagePermission = usePermission(UserPermission.AdminSettingsManage);

  const hasUserSupportReadPermission = usePermission(UserPermission.SupportRead);
  const hasCalendarReadPermission =
    usePermission(UserPermission.AdminCalendarRead) || usePermission(UserPermission.UserCalendarRead);

  const hasReportPermission = usePermission(UserPermission.UserReportRead);
  const AdminStatisticsDashboardRead = usePermission(UserPermission.AdminStatisticsDashboardRead);

  const handleSystemSettingsOpen = () => {
    setOpenSystemSettings(true);
  };

  const handleSystemSettingsClose = () => {
    setOpenSystemSettings(false);
  };

  const homeButtonTooltip = viewType === 'fullwidthForExaminer' ? 'Dashboard' : 'Strona główna';

  const isPathAvailableForContextualHelp = () => {
    if (!hasUserSupportReadPermission) return false;

    const path = '/' + window.location.pathname.split('/')[1];
    if (
      (path === Paths.assessment ||
        path === Paths.solution ||
        path === Paths.sampleAssessmentGroupSolutionAssignment ||
        path === Paths.sampleAssessmentRepository ||
        path === Paths.sampleAssessment ||
        path === Paths.assessmentSampleAssessmentGroup) &&
      userAssessmentSessionInfo.account_type === 'examiner'
    )
      return true;
  };

  const handleContextualHelpClick = () => {
    dispatch(contextualHelpActions.toogleContextualHelp(!isContextualHelp));
  };

  const openReports = () => {
    let url = '';
    if (EnvWindow.REACT_APP_REPORTS_URL && EnvWindow.REACT_APP_REPORTS_URL.length > 0)
      url = `${EnvWindow.REACT_APP_REPORTS_URL}/Reports?jwt=${user.access_token}`;
    else url = `${EnvWindow.REACT_APP_SERVER_URL}/Reports?jwt=${user.access_token}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const getActionsMenuIcon = (): JSX.Element | null => {
    const items: ActionMenuItem[] = [];
    if (hasUserSupportReadPermission) {
      items.push({
        text: 'Helpdesk',
        onClick: () => {
          handleSupportClick();
        },
        icon: <ContactSupportOutlinedIcon fill={fill} className={classes.navMenuIcon} style={{ width: '22px' }} />,
      });
    }
    if (hasAdminSettingsManagePermission) {
      items.push({
        text: 'Ustawienia systemowe',
        onClick: () => {
          handleSystemSettingsOpen();
        },
        icon: <SettingsOutlinedIcon fill={fill} className={classes.navMenuIcon} style={{ width: '22px' }} />,
      });
    }
    if (hasCalendarReadPermission) {
      items.push({
        text: 'Kalendarz zdarzeń',
        onClick: () => {
          history.push(Paths.calendar);
        },
        icon: <EventNoteIcon fill={fill} className={classes.navMenuIcon} style={{ width: '22px' }} />,
      });
    }
    if (AdminStatisticsDashboardRead) {
      items.push({
        text: 'Statystyki',
        onClick: () => {
          history.push(Paths.statistics);
        },
        icon: <TrendingUpIcon fill={fill} className={classes.navMenuIcon} style={{ width: '22px' }} />,
      });
    }
    if (hasReportPermission) {
      items.push({
        text: 'Raporty',
        onClick: () => {
          openReports();
        },
        icon: <InsertChartOutlinedIcon fill={fill} className={classes.navMenuIcon} style={{ width: '22px' }} />,
        style: { borderTop: '1px solid #ccc', paddingTop: '10px' },
      });
    }
    return <ActionsMenu menuItems={items} tooltipTitle="Ustawienia" isNotInTable={true} />;
  };

  const isMinimized = !!sessionStorage.getItem(NAVBAR_ISMINIMIZED_KEY);

  return (
    <>
      <LoaderAlert hideAlerts={dashboardView} />
      <header
        style={{
          position: 'relative',
          left: isSkipLink ? '240px' : '0px',
          top: '0px',
          width: isSkipLink ? 'calc(100% - 250px)' : '',
          padding: '5px',
        }}
      >
        <Grid container justifyContent={'space-between'}>
          <Grid item>
            {!examinerView && !dashboardView && !inMailboxAlready && viewType !== 'fullwidthForExaminer' && (
              <TooltipOne title="Menu" placement="bottom">
                <IconButton disableRipple onClick={handleMenuHamburgerClick} style={{ padding: '5px' }}>
                  <MenuIcon aria-expanded={!isMinimized} style={{ color: fill, padding: '0' }} />
                </IconButton>
              </TooltipOne>
            )}
            {inMailboxAlready && (
              <></>
              /*<TooltipOne title="Menu" placement="bottom">
                <IconButton disableRipple onClick={handleMenuHambugerClickMailbox} style={{ padding: '5px' }}>
                  <MenuIcon style={{ color: fill, padding: '0' }} />
                </IconButton>
            </TooltipOne>*/
              /* 22524 */
            )}
            {((viewType && ['admin', 'fullwidthForExaminer', 'fullwidthWithMenu'].includes(viewType)) ||
              examinerView) && (
              <TooltipOne title={homeButtonTooltip} placement="bottom">
                <IconButton disableRipple className={classes.backIcon} onClick={handleHomeClick}>
                  <img src={Dashboard} alt={'Cofnij'} />
                </IconButton>
              </TooltipOne>
            )}
          </Grid>
          <Grid item>
            {isPathAvailableForContextualHelp() && (
              <TooltipOne title="Pomoc kontekstowa" placement="top">
                <IconButton disableRipple onClick={handleContextualHelpClick}>
                  {isContextualHelp ? (
                    <ContextualHelpIconHover fill={fill} className={classes.navIcon} />
                  ) : (
                    <ContextualHelpIcon fill={fill} className={classes.navIcon} />
                  )}
                </IconButton>
              </TooltipOne>
            )}
            {hasUserSupportReadPermission && (
              <TooltipOne title="Helpdesk - otworzy się w nowej karcie" placement="top">
                <IconButton disableRipple onClick={handleSupportClick}>
                  <ContactSupportOutlinedIcon fill={fill} className={classes.navIcon} />
                </IconButton>
              </TooltipOne>
            )}
            <MailMenu messageModalIsOpen={setIsMessageModalOpen} />
            <NotificationsMenu />
            {getActionsMenuIcon()}
            {user && (
              <div className={classes.userProfileContainer}>
                <AccountCircleOutlinedIcon
                  className={classes.navIcon}
                  style={{ paddingLeft: '10px', paddingRight: '10px' }}
                />
                {userName ? userName : user.profile.name}
                <NavbarMenu viewType={viewType} userNewName={setUserName} />
              </div>
            )}
          </Grid>
        </Grid>
        {openSystemSettings && <SystemSettings onClose={handleSystemSettingsClose} />}
      </header>
    </>
  );
};

export default React.memo(Navbar);
